import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { X, Radio, Zap, MapPin, User, Cpu, Fuel, Wrench, Droplets, Battery, Calendar, StickyNote, ClipboardList, Navigation } from 'lucide-react';
import { franklinAPI } from '@shared/apiClient';
import { formatDate, formatDateTime, getStatusColor, getStatusText } from '@shared/utils';
import { DetailSection, DetailRow, StatPill, getDirectionsUrl } from './DetailSection';

export default function SiteDetailsModal({ site, siteType, workOrder, isOpen, onClose }) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const { data: franklinFullData } = useQuery(
    ['franklinSiteDetail', site?.id],
    () => franklinAPI.getSite(site.id),
    { enabled: isOpen && siteType === 'franklin' && !!site?.id }
  );

  const fullSite = siteType === 'franklin' && franklinFullData?.data?.site
    ? franklinFullData.data.site
    : site || {};

  const serviceHistory = siteType === 'franklin'
    ? (fullSite.serviceCalls || [])
    : [];

  const isFranklin = siteType === 'franklin';
  const titlePrefix = isFranklin ? 'Franklin' : 'C Spire';
  const displayName = fullSite.locationName || fullSite.siteId || 'Site';

  const formatCoordinate = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(parsed)) return value;
    return parsed.toFixed(6);
  };

  const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const getServiceMonths = (months) => {
    if (!months) return null;
    const normalized = Array.isArray(months) ? months : [];
    return normalized.map(m => monthLabels[m - 1]).filter(Boolean).join(', ');
  };

  if (!isOpen) return null;

  const lat = formatCoordinate(fullSite.latitude);
  const lon = formatCoordinate(fullSite.longitude);
  const hasCoords = lat && lon;
  const mapUrl = hasCoords ? `https://maps.google.com/?q=${fullSite.latitude},${fullSite.longitude}` : null;

  const headerGradient = isFranklin
    ? 'from-amber-500 to-amber-600'
    : 'from-blue-600 to-blue-700';
  const headerSubtextColor = isFranklin ? 'text-amber-100' : 'text-blue-200';
  const headerBtnColor = isFranklin ? 'text-amber-100' : 'text-blue-200';
  const SiteIcon = isFranklin ? Zap : Radio;

  return (
    <div className="fixed inset-0 z-50 bg-slate-50">
      <div className="fixed inset-0 flex h-full w-full flex-col">
        {/* Gradient Header */}
        <div className={`bg-gradient-to-r ${headerGradient} px-6 py-5`}>
          <div className="flex items-start justify-between">
            <div>
              <p className={`text-xs font-medium uppercase tracking-wider ${headerSubtextColor}`}>{titlePrefix} Site Details</p>
              <h3 className="text-xl font-bold text-white mt-1">{displayName}</h3>
            </div>
            <button
              onClick={onClose}
              className={`rounded-full p-2 ${headerBtnColor} hover:bg-white/10 hover:text-white transition-colors`}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          {site && (
            <div className="flex flex-wrap gap-2 mt-4">
              <StatPill
                label="Status"
                value={fullSite.isActive !== false ? 'Active' : 'Inactive'}
                variant={fullSite.isActive !== false ? 'success' : 'danger'}
              />
              {fullSite.generatorType && (
                <StatPill label="Generator" value={fullSite.generatorType} />
              )}
              {workOrder?.workOrderNumber && (
                <StatPill label="Work Order" value={`#${workOrder.workOrderNumber}`} />
              )}
              {isFranklin && fullSite.lastServiceDate && (
                <StatPill label="Last Service" value={formatDate(fullSite.lastServiceDate)} />
              )}
            </div>
          )}
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y px-4 py-5">
          {!site ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              Loading site details...
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {/* Site Identification */}
              <DetailSection icon={SiteIcon} title="Site Identification" color={isFranklin ? 'amber' : 'blue'}>
                <DetailRow label="Site ID" value={fullSite.siteId} />
                {isFranklin && <DetailRow label="Company" value={fullSite.company} />}
                <DetailRow label="Location Name" value={fullSite.locationName} />
                {isFranklin && <DetailRow label="District" value={fullSite.district} />}
                {isFranklin && <DetailRow label="County" value={fullSite.county} />}
                {workOrder && <DetailRow label="Work Order #" value={workOrder.workOrderNumber} />}
                {workOrder?.subject && <DetailRow label="WO Subject" value={workOrder.subject} />}
              </DetailSection>

              {/* Address & Location */}
              <DetailSection icon={MapPin} title="Address & Location" color="amber">
                <DetailRow label="Address" value={isFranklin ? fullSite.address : fullSite.addressLine1} />
                <DetailRow label="City" value={fullSite.city} />
                <DetailRow label="State" value={fullSite.state} />
                <DetailRow label="ZIP Code" value={fullSite.zipCode} />
                <DetailRow label="Coordinates" value={hasCoords ? (
                  <a href={mapUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                    {lat}, {lon}
                  </a>
                ) : null} />
              </DetailSection>

              {/* Contact (CSpire only) */}
              {!isFranklin && (fullSite.contactName || fullSite.contactEmail || fullSite.contactPhone) && (
                <DetailSection icon={User} title="Contact" color="blue">
                  <DetailRow label="Name" value={fullSite.contactName} />
                  <DetailRow label="Email" value={fullSite.contactEmail ? (
                    <a href={`mailto:${fullSite.contactEmail}`} className="text-blue-600 hover:underline">{fullSite.contactEmail}</a>
                  ) : null} />
                  <DetailRow label="Phone" value={fullSite.contactPhone ? (
                    <a href={`tel:${fullSite.contactPhone}`} className="text-blue-600 hover:underline">{fullSite.contactPhone}</a>
                  ) : null} />
                </DetailSection>
              )}

              {/* Generator Information */}
              <DetailSection icon={Cpu} title="Generator Information" color="green">
                <DetailRow label="Type" value={fullSite.generatorType} />
                <DetailRow label="Model" value={fullSite.generatorModel} />
                <DetailRow label="Serial Number" value={fullSite.generatorSerialNumber} />
                <DetailRow label="Size / Capacity" value={fullSite.generatorSize} />
                <DetailRow label="Exercise Time" value={fullSite.generatorExerciseTime} />
              </DetailSection>

              {/* Power & Fuel (Franklin-specific) */}
              {isFranklin && (fullSite.backupPower || fullSite.tankSize || fullSite.monthYearPlaced || fullSite.herringGasRef) && (
                <DetailSection icon={Fuel} title="Power & Fuel Setup" color="orange">
                  <DetailRow label="Backup Power" value={fullSite.backupPower} />
                  <DetailRow label="Tank Size" value={fullSite.tankSize} />
                  <DetailRow label="Month/Year Placed" value={fullSite.monthYearPlaced} />
                  <DetailRow label="Herring Gas Ref" value={fullSite.herringGasRef} />
                </DetailSection>
              )}

              {/* Maintenance Parts */}
              <DetailSection icon={Wrench} title="Maintenance Parts" color="orange">
                <DetailRow label="Air Filter" value={fullSite.airFilter} />
                <DetailRow label="Fuel Filter 1" value={fullSite.fuelFilter1} />
                <DetailRow label="Fuel Filter 2" value={fullSite.fuelFilter2} />
                <DetailRow label="Oil Filter 1" value={fullSite.oilFilter1} />
                <DetailRow label="Oil Filter 2" value={fullSite.oilFilter2} />
              </DetailSection>

              {/* Fluids */}
              <DetailSection icon={Droplets} title="Fluids" color="cyan">
                <DetailRow label="Fuel Type" value={fullSite.fuelType} />
                <DetailRow label="Fuel Additive" value={fullSite.fuelAdditive} />
                <DetailRow label="Oil Type" value={fullSite.oilType} />
                <DetailRow label="Oil Amount" value={fullSite.oilAmount} />
              </DetailSection>

              {/* Battery */}
              <DetailSection icon={Battery} title="Battery" color="yellow">
                <DetailRow label="Battery Size" value={fullSite.batterySize} />
                <DetailRow label="Battery Date Code" value={fullSite.batteryDateCode} />
                <DetailRow label="Battery Date Changed" value={fullSite.batteryDateChanged ? formatDate(fullSite.batteryDateChanged) : null} />
              </DetailSection>

              {/* Service Information */}
              <DetailSection icon={Calendar} title="Service Information" color="purple">
                {isFranklin && <DetailRow label="Service Months" value={getServiceMonths(fullSite.serviceCycleMonths)} />}
                {isFranklin && <DetailRow label="Oil Change Month" value={fullSite.oilChangeMonth ? monthLabels[fullSite.oilChangeMonth - 1] : null} />}
                <DetailRow label="Last Service" value={fullSite.lastServiceDate ? formatDate(fullSite.lastServiceDate) : null} />
              </DetailSection>

              {/* Service History (Franklin) - full width */}
              {isFranklin && (
                <DetailSection icon={ClipboardList} title="Service History" color="purple" span>
                  {serviceHistory.length === 0 ? (
                    <div className="text-sm text-gray-500 py-2">No service history found.</div>
                  ) : (
                    <div className="space-y-3 mt-1">
                      {serviceHistory.map((sc) => {
                        const statusBorderColor = sc.status === 'completed'
                          ? 'border-l-emerald-400'
                          : sc.status === 'in_progress'
                            ? 'border-l-blue-400'
                            : 'border-l-slate-300';
                        return (
                          <div key={sc.id} className={`rounded-lg border border-slate-200 bg-slate-50/50 p-3 border-l-4 ${statusBorderColor}`}>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`status-badge ${getStatusColor(sc.status)}`}>
                                {getStatusText(sc.status)}
                              </span>
                              {sc.isWarranty && (
                                <span className="rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                                  Warranty
                                </span>
                              )}
                              {sc.technician && (
                                <span className="text-xs text-gray-500">
                                  Tech: {sc.technician.firstName} {sc.technician.lastName}
                                </span>
                              )}
                            </div>
                            <div className="mt-2 text-sm text-gray-600">
                              {sc.scheduledDate && (
                                <div><span className="font-medium">Scheduled:</span> {formatDateTime(sc.scheduledDate)}</div>
                              )}
                              {sc.completedDate && (
                                <div><span className="font-medium">Completed:</span> {formatDateTime(sc.completedDate)}</div>
                              )}
                            </div>
                            {sc.description && (
                              <p className="mt-2 text-sm text-gray-700">{sc.description}</p>
                            )}
                            {sc.workPerformed && (
                              <p className="mt-1 text-sm text-gray-600">
                                <span className="font-medium">Work Performed:</span> {sc.workPerformed}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </DetailSection>
              )}

              {/* Notes - full width */}
              {fullSite.notes && (
                <DetailSection icon={StickyNote} title="Notes" color="slate" span>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{fullSite.notes}</p>
                </DetailSection>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-6 py-4">
          <div>
            {site && (() => {
              const addr = isFranklin ? fullSite.address : fullSite.addressLine1;
              const dirUrl = getDirectionsUrl({ latitude: fullSite.latitude, longitude: fullSite.longitude, address: addr, city: fullSite.city, state: fullSite.state, zipCode: fullSite.zipCode });
              return dirUrl ? (
                <a
                  href={dirUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-400"
                >
                  <Navigation className="h-4 w-4 mr-1.5" />
                  Directions
                </a>
              ) : null;
            })()}
          </div>
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
